// src/components/parent/ProviderSearch.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { searchProviders } from '../../services/firestore';
import { FaList, FaPhone, FaEnvelope, FaStar, FaRegStar, FaChild, FaHome, FaBuilding, FaCheckCircle, FaMap, FaMapMarkerAlt } from 'react-icons/fa';
import StarRating from '../common/StarRating';
import ProviderMap from '../common/ProviderMap';

const ProviderSearch = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  
  // Configuration data
  const config = {
    activities: [
      'Arts & Crafts', 'Music & Movement', 'Outdoor Play', 'Story Time', 'Science Experiments',
      'Cooking', 'Field Trips', 'Physical Education', 'Gardening', 'Dramatic Play'
    ],
    programs: [
      'Early Learning', 'Montessori', 'Reggio Emilia', 'Play-based', 'Waldorf',
      'Bilingual', 'Special Needs', 'Outdoor Learning', 'After School', 'Summer Camp'
    ],
    languages: [
      'English', 'French', 'Spanish', 'Mandarin', 'Hindi', 'Arabic', 'Punjabi',
      'German', 'Italian', 'Portuguese', 'Russian', 'Japanese'
    ],
    ageGroups: [
      { value: 'Infant/Toddler', label: 'Infant/Toddler (6 weeks–29 months)' },
      { value: 'Preschool', label: 'Preschool (30 months–Kindergarten entry)' },
      { value: 'School-Age', label: 'School-Age (Grade 1–13 years)' }
    ],
    providerTypes: [
      { value: 'center', label: 'Daycare Center' },
      { value: 'home', label: 'Daycare Home' }
    ]
  };
  
  // State
  const [filters, setFilters] = useState({
    childAge: '', type: '', licensed: '', programs: [], activities: [], languages: []
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const defaultChildAgeSet = React.useRef(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [userLocation, setUserLocation] = useState(null);
  
  // Load user preferences from profile
  useEffect(() => {
    if (
      userProfile?.profile &&
      !defaultChildAgeSet.current // only run once
    ) {
      const profile = userProfile.profile;
      
      if (profile.children?.length > 0 && profile.children[0].age) {
        const childAge = parseInt(profile.children[0].age);
        const ageCategory = childAge < 3 ? 'Infant/Toddler' : 
                          childAge < 6 ? 'Preschool' : 'School-Age';
        
        setFilters(prev => ({ ...prev, childAge: ageCategory }));
      }
      
      if (profile.languages?.length > 0) {
        setFilters(prev => ({ ...prev, languages: profile.languages }));
      }
      
      if (profile.activityPreferences?.length > 0) {
        setFilters(prev => ({ ...prev, activities: profile.activityPreferences }));
      }
      
      // Set user location if available
      if (profile.location) {
        setUserLocation(profile.location);
      }
      
      defaultChildAgeSet.current = true; // mark as set
      setLoading(false);
    }
  }, [userProfile]);
  
  // Get user's current location
  useEffect(() => {
    if (!userLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.log('Error getting location:', error);
        }
      );
    }
  }, [userLocation]);
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle checkbox filter changes
  const handleCheckboxFilterChange = (e, field) => {
    const { value, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value] 
        : prev[field].filter(item => item !== value)
    }));
  };
  
  // Handle boolean filter changes
  const handleBooleanFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      childAge: '', type: '', licensed: '', programs: [], activities: [], languages: []
    });
  };
  
  // Perform search
  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { providers } = await searchProviders(filters);
      setResults(providers || []);
      setSearchPerformed(true);
      setSelectedProvider(null);
    } catch (err) {
      console.error('Error searching for providers:', err);
      setError('Error searching for providers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Function to consistently get provider display name
  const getProviderDisplayName = (provider) => {
    // First try to get displayName from profile object
    if (provider.profile?.displayName) {
      return provider.profile.displayName;
    }
    // Fall back to displayName if it exists on the provider object
    if (provider.displayName) {
      return provider.displayName;
    }
    // Fall back to name as a last resort
    return provider.name || 'Provider';
  };
  
  // Handle View Full Profile button click
  const handleViewProfile = (providerId) => {
    navigate(`/provider/${providerId}`);
  };
  
  // Handle Contact Provider button click
  const handleContactProvider = (providerId) => {
    navigate(`/messages`, { 
      state: { initiateChat: true, receiverId: providerId }
    });
  };
  
  // Format list for display
  const formatList = list => !list || list.length === 0 ? 'N/A' : list.join(', ');
  
  // Handle provider click
  const handleProviderClick = (provider) => {
    setSelectedProvider(provider);
  };
  
  return (
    <div className="provider-search">
      <h1>Find Childcare Providers</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="search-container">
        <div className="filters-container">
          <h2>Search Filters</h2>
          
          {/* Basic Filters */}
          <div className="form-section">
            <h3>Basic Filters</h3>
            
            <div className="form-group">
              <label htmlFor="childAge">Child Age Group:</label>
              <select id="childAge" name="childAge" value={filters.childAge} onChange={handleFilterChange}>
                <option value="">All Age Groups</option>
                {config.ageGroups.map(group => (
                  <option key={group.value} value={group.value}>{group.label}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="type">Childcare Type:</label>
              <select id="type" name="type" value={filters.type} onChange={handleFilterChange}>
                <option value="">All Types</option>
                {config.providerTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="licensed">Licensed Status:</label>
              <select id="licensed" name="licensed" value={filters.licensed} onChange={handleBooleanFilterChange}>
                <option value="">Any</option>
                <option value="true">Licensed</option>
                <option value="false">Unlicensed</option>
              </select>
            </div>
          </div>
          
          {/* Advanced Filters */}
          {/* Removed Programs & Activities filter section */}
          
          <div className="search-actions">
            <button 
              type="button"
              className="reset-button" 
              onClick={handleResetFilters} 
              disabled={loading}
            >
              Reset Filters
            </button>
            <button 
              type="button"
              className="search-button" 
              onClick={handleSearch} 
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search Providers'}
            </button>
          </div>
        </div>
        
        {/* Search Results */}
        <div className="search-results-container">
          <div className="results-header">
            <h2>Search Results</h2>
            {results.length > 0 && (
              <div className="results-stats">
                <p className="results-count">Found {results.length} providers</p>
                <div className="view-toggle">
                  <button 
                    className={`toggle-button ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    <FaList size={16} /> List
                  </button>
                  <button 
                    className={`toggle-button ${viewMode === 'map' ? 'active' : ''}`}
                    onClick={() => setViewMode('map')}
                  >
                    <FaMap size={16} /> Map
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {loading ? (
            <div className="loading-container">
              <p>Searching for providers...</p>
              {/* You could add a spinner here */}
            </div>
          ) : searchPerformed && results.length === 0 ? (
            <div className="no-results">
              <h3>No providers found matching your criteria</h3>
              <p>Try adjusting your filters or search in a different area.</p>
            </div>
          ) : searchPerformed && (
            <div className="results-content">
              {viewMode === 'list' ? (
                <div className="provider-list">
                  {results.map((provider) => (
                    <div className="provider-card" key={provider.id} onClick={() => handleProviderClick(provider)}>
                      <div className="provider-card-header">
                        <h3>{getProviderDisplayName(provider)}</h3>
                        {provider.licensed && (
                          <div className="licensed-badge">
                            <FaCheckCircle size={14} className="icon-margin-right" />
                            Licensed
                          </div>
                        )}
                      </div>
                      
                      <div className="provider-card-body">
                        {provider.profile?.address && (
                          <div className="provider-address">
                            <span>{provider.profile.address}</span>
                          </div>
                        )}
                        
                        <div className="provider-programs">
                          {provider.profile?.programs && provider.profile.programs.length > 0 ? (
                            provider.profile.programs.slice(0, 3).map((program, idx) => (
                              <span key={idx} className="tag program-tag">{program}</span>
                            ))
                          ) : null}
                        </div>
                        
                        <div className="provider-rating">
                          <StarRating value={provider.averageRating || 0} readonly size="small" />
                          <span className="reviews-count">
                            ({provider.reviewCount || 0} reviews)
                          </span>
                        </div>
                      </div>
                      
                      <div className="provider-card-footer">
                        <button 
                          className="view-profile-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewProfile(provider.id);
                          }}
                        >
                          View Full Profile
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="provider-map-container" style={{ height: '600px' }}>
                  <ProviderMap 
                    providers={results}
                    userLocation={userLocation}
                    onProviderClick={handleProviderClick}
                    onMapClick={() => setSelectedProvider(null)}
                  />
                </div>
              )}
              
              {/* Selected Provider Panel - Small Popup */}
              {selectedProvider && (
                <div className="provider-popup-overlay" onClick={() => setSelectedProvider(null)}>
                  <div className="provider-popup-small" onClick={(e) => e.stopPropagation()}>
                    <button className="popup-close-btn" onClick={() => setSelectedProvider(null)}>×</button>
                    
                    <div className="popup-header">
                      <h3>{getProviderDisplayName(selectedProvider)}</h3>
                      <div className="popup-type">
                        {selectedProvider.profile?.type === 'center' ? 'Daycare Center' : 'Daycare Home'}
                        {selectedProvider.profile?.licensed && ' • Licensed'}
                      </div>
                    </div>
                    
                    <div className="popup-content">
                      {selectedProvider.profile?.address && (
                        <p className="popup-address">
                          <FaMapMarkerAlt size={12} style={{ marginRight: '6px', color: '#666' }} />
                          {selectedProvider.profile.address}
                        </p>
                      )}
                      
                      {selectedProvider.profile?.phone && (
                        <p className="popup-phone">
                          <FaPhone size={12} style={{ marginRight: '6px', color: '#666' }} />
                          {selectedProvider.profile.phone}
                        </p>
                      )}
                      
                      {selectedProvider.profile?.programs && selectedProvider.profile.programs.length > 0 && (
                        <div className="popup-programs">
                          <strong>Programs:</strong>
                          <div className="popup-tags">
                            {selectedProvider.profile.programs.slice(0, 3).map((program, idx) => (
                              <span key={idx} className="popup-tag">{program}</span>
                            ))}
                            {selectedProvider.profile.programs.length > 3 && (
                              <span className="popup-tag">+{selectedProvider.profile.programs.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="popup-rating">
                        <StarRating value={selectedProvider.averageRating || 0} readonly size="small" />
                        <span className="popup-reviews">({selectedProvider.reviewCount || 0} reviews)</span>
                      </div>
                    </div>
                    
                    <div className="popup-actions">
                      <button className="popup-btn popup-btn-primary" onClick={() => handleViewProfile(selectedProvider.id)}>
                        View Profile
                      </button>
                      <button className="popup-btn popup-btn-secondary" onClick={() => handleContactProvider(selectedProvider.id)}>
                        Contact
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderSearch;